import { useEffect, useRef } from 'react';
import { MAX_VIDEO_NOTE_MS, type Facing, type Preview } from '../lib/capture.ts';
import { reportClientError } from '../lib/errorReporter.ts';
import { formatDuration } from '../lib/format.ts';
import { describeTrouble, keepPainting } from '../lib/livePreview.ts';
import type { RecorderState, Recording } from '../lib/useRecorder.ts';
import {
  FlipCameraIcon,
  LockIcon,
  MicIcon,
  SwipeUpArcIcon,
  TrashIcon,
  VideoIcon,
} from './MediaIcons.tsx';
import './RecordControl.css';

/**
 * What the composer bar becomes while a кружочек or a voice message is being
 * recorded: the pill in place of the text field, the button that started it —
 * or, once locked, the trash-and-pause pair that replaces it — and, for a
 * кружочек, the circle floating above the bar showing what the camera sees.
 *
 * All of it is presentational. The gesture that drives them is in
 * lib/useRecorder.ts, which is where the thresholds and the state machine
 * belong; this file only draws what that hook says is happening.
 */

/** Six bars, staggered so they read as sound rather than as a metronome. */
const LIVE_BARS = [0, 1, 2, 3, 4, 5].map((index) => ({
  height: 6 + ((index * 7) % 15),
  duration: 0.55 + (index % 3) * 0.12,
  delay: index * 0.08,
}));

export function RecordPill({ recording }: { recording: Recording }) {
  // Nothing is being recorded yet, and the pill must not imply otherwise: no red
  // dot, no clock, and the words say what is actually happening. Someone who
  // waits for this to turn into a timer before speaking loses nothing.
  if (recording.state === 'opening') {
    return (
      <div className="record-pill">
        <span className="record-spinner" />
        <span className="record-opening" role="status">
          {recording.mode === 'voice' ? 'Включаем микрофон…' : 'Включаем камеру…'}
        </span>
      </div>
    );
  }

  // The other end of the same rule: recording has stopped, so no red dot and no
  // clock either — what is left is the pieces of a flipped take being stitched
  // into one file, which takes seconds and is worth naming. A take that was
  // never flipped never reaches this state.
  if (recording.state === 'joining') {
    return (
      <div className="record-pill">
        <span className="record-spinner" />
        <span className="record-opening" role="status">
          Собираем кружочек {Math.round(recording.joined * 100)}%
        </span>
      </div>
    );
  }

  return (
    <div className="record-pill">
      <span className="record-dot" />
      <span className="record-time">
        {formatDuration(recording.elapsedMs)}
        {/* A кружочек has a ceiling and the clock says so; a voice message has
            none, and a denominator would invent one. */}
        {recording.mode === 'video' && (
          <span className="record-cap"> / {formatDuration(MAX_VIDEO_NOTE_MS)}</span>
        )}
      </span>

      {recording.mode === 'voice' && (
        <div className="record-bars" aria-hidden="true">
          {LIVE_BARS.map((bar, index) => (
            <span
              key={index}
              className="record-bar"
              style={{
                height: `${bar.height}px`,
                animationDuration: `${bar.duration}s`,
                animationDelay: `${bar.delay}s`,
              }}
            />
          ))}
        </div>
      )}

      <span className="record-hint">
        {recording.locked ? 'Запись закреплена' : '‹ Слайд для отмены'}
      </span>
    </div>
  );
}

/**
 * The circle a take is watched through, and the machinery that keeps a picture
 * in it. Two callers frame the same take here: RecordPreview below, for a
 * кружочек recorded from the composer, and the camera screen's «Кружочек» mode —
 * which is why the circle knows nothing about scrims, rings or flip buttons.
 *
 * What is in this circle is what is being written to the file, because the
 * element here *is* the one the capture is recording — moved into the page, not
 * copied into it. Which is also why it is mounted by hand: it belongs to the
 * take, so React is given somewhere to put it rather than being asked to own it.
 * The take keeps it in the document either way — Safari will not decode an
 * element that is not — so this hands it back rather than removing it.
 *
 * On iOS the same sentence is true by a different route and the circle here is
 * empty: the picture is a layer above the WebView fed by the AVCaptureSession
 * the file is being written from, and what this hands it is a rectangle. Which
 * of the two it is, this component learns from the take rather than from the
 * platform — see Preview in lib/capture.ts — so there is no Capacitor anywhere
 * in this file and nothing here has to know which app it is running in.
 *
 * `facing` is the one thing the circle is not allowed to show faithfully. A
 * front camera is drawn mirrored, because a кружочек is recorded by someone
 * watching their own face and a self-view that moves the wrong way is unusable
 * for framing — the same rule as the camera screen's viewfinder. The mirror is
 * CSS on the host rather than on the element, so what is encoded is untouched:
 * the recipient gets the unmirrored picture, as they do from any camera.
 */
export function LiveCircle({
  source,
  facing,
  flipping = false,
}: {
  source: Preview;
  facing: Facing;
  /** A swap in flight: the picture hides behind the placeholder for it. */
  flipping?: boolean;
}) {
  const host = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const parent = host.current;
    if (!parent) return;

    // Inside the iOS app there is nothing to mount: the picture is an
    // AVCaptureVideoPreviewLayer above the WebView, and the circle below is an
    // empty hole it is aimed at. Everything the web branch does to keep a moved
    // <video> painting — the style shuffle, keepPainting, the hand-back to the
    // body — is machinery for a problem that does not exist when the frames
    // never enter WebKit at all. See lib/nativeVideoNote.ts.
    if (!(source instanceof HTMLVideoElement)) {
      const measure = () => {
        const { x, y, width, height } = parent.getBoundingClientRect();
        source.setFrame({ x, y, width, height });
      };
      measure();
      // Size and position both, from two sources: the circle is 84vw-capped so
      // it resizes with the viewport, and it is centred in a fixed layer so it
      // *moves* when the viewport changes without necessarily changing size —
      // a rotation, or the keyboard coming up under a locked take.
      const resized = new ResizeObserver(measure);
      resized.observe(parent);
      window.addEventListener('resize', measure);
      window.visualViewport?.addEventListener('resize', measure);
      return () => {
        resized.disconnect();
        window.removeEventListener('resize', measure);
        window.visualViewport?.removeEventListener('resize', measure);
        // Put away rather than left behind a screen that no longer has a circle
        // on it. The take releases the camera itself; this is only the layer.
        source.setFrame(null);
      };
    }

    // The take hides the element off-screen so that it can be decoded before
    // there is a circle to put it in; see startVideoNote. Those styles are what
    // stands between it and the CSS below, and they go back on the way out.
    const hidden = source.style.cssText;
    source.style.cssText = '';
    parent.appendChild(source);
    // Moving a `<video>` in the DOM is what turns the circle into a grey disc on
    // iOS while the recording itself comes out perfectly — the file is written
    // from the track, not from this element. Playing it again after the move was
    // the first repair and it fixed some takes and not others, because WebKit
    // queues that pause rather than performing it; keepPainting watches for the
    // frames instead of assuming them. See lib/livePreview.ts.
    const stopWatching = keepPainting(source);
    return () => {
      // Filed against the take that had it rather than counted in the moment: a
      // sender holding a button is in no position to read anything, and what
      // this answers — how often the circle is still grey, and whether the
      // repair works when it is — is a question for «Ошибки на устройствах» on
      // /admin. Same trade as the кружочек's failed stitch in capture.ts.
      const trouble = stopWatching();
      if (trouble) {
        reportClientError({
          kind: 'crash',
          detail: `videoPreview${trouble.painted ? '' : ' blank'}`,
          message: describeTrouble(trouble),
        });
      }
      source.style.cssText = hidden;
      // Handed back rather than removed: stopping the camera is the capture's
      // business, and an element out of the document stops decoding on Safari —
      // which would cost the poster, taken off this element after the bytes are
      // collected. Unless the take has already ended and released it, in which
      // case putting it back would leave a dead <video> in the page for good.
      if (source.srcObject) {
        document.body.appendChild(source);
        // Paused by the move, same as above, and a paused element is one the
        // poster would be drawn off blank.
        void source.play().catch(() => {});
      }
    };
  }, [source]);

  return (
    <div
      className="record-preview-circle"
      data-facing={facing}
      data-flipping={flipping || undefined}
      ref={host}
    />
  );
}

/**
 * The live кружочек while it is being recorded from the composer: a large circle
 * in the middle of the screen, over a scrim that dims the thread behind it. It
 * makes the framing answerable during the take rather than only at the trim
 * screen.
 *
 * The flip button turns the camera round then and there. What that costs is not
 * visible from here and is worth knowing: on the web the recording is cut and
 * restarted on the other camera, and the pieces are stitched into one file
 * before the кружочек is sent (see startVideoNote) — inside the iOS app the
 * capture session changes camera under a writer that goes on writing, and there
 * is nothing to stitch. It is absent when /admin has switched the flip off, and
 * absent on a browser that could not do the stitching.
 *
 * It used to be a 172px circle in the corner, and a corner is precisely where a
 * face being recorded is not looked at. Taking the screen over is what makes the
 * framing something the sender is actually watching; the scrim stops short of
 * the composer bar, which stays lit and usable above it, because the gesture
 * holding the take together is still down there.
 */
export function RecordPreview({
  source,
  facing,
  canFlip,
  onFlip,
  elapsedMs,
  flipping,
}: {
  source: Preview;
  facing: Facing;
  canFlip: boolean;
  onFlip: () => void;
  /** Feeds the ring around the circle — how much of the cap is spent. */
  elapsedMs: number;
  /** A swap in flight: the picture hides behind the placeholder for it. */
  flipping: boolean;
}) {
  // The ring is the take's clock drawn where the sender is actually looking:
  // at themselves, not at the pill. It fills clockwise against the cap, and
  // turns red for the last ten seconds — the point of the colour is that «wrap
  // it up» has to arrive without reading anything. Same conic-gradient device
  // as the playback ring on the bubble (.note in MessageThread.css).
  const spent = Math.min(1, elapsedMs / MAX_VIDEO_NOTE_MS);
  const closing = MAX_VIDEO_NOTE_MS - elapsedMs <= 10_000;
  const arc = closing ? 'var(--rec-active)' : 'var(--accent)';

  return (
    <div className="record-preview">
      <div
        className="record-preview-ring"
        style={{ background: `conic-gradient(${arc} ${spent * 100}%, rgba(255, 255, 255, 0.25) 0)` }}
      >
        <LiveCircle source={source} facing={facing} flipping={flipping} />
      </div>
      {/* Outside the circle rather than on it. Inside, the round mask clipped it
          and it shared its corner with the picture; here a second finger can
          find it without hunting, which is the only way it is ever pressed —
          the first one is holding the recording. */}
      {canFlip && (
        <button
          type="button"
          className="record-preview-flip"
          title="Сменить камеру"
          aria-label="Сменить камеру"
          // The hold that started the take is tracked on the window, so that
          // second finger would otherwise be read as the first one letting go —
          // and the кружочек would send itself mid-flip. Both ends of this tap
          // stop where they land.
          onPointerDown={(event) => event.stopPropagation()}
          onPointerUp={(event) => event.stopPropagation()}
          onClick={onFlip}
        >
          <FlipCameraIcon size={22} />
        </button>
      )}
    </div>
  );
}

export function RecordButtons({ recorder }: { recorder: RecorderState }) {
  const { mode, recording } = recorder;
  const icon = mode === 'voice' ? <MicIcon /> : <VideoIcon />;

  // Hands-free: the finger is gone and the two remaining outcomes need buttons
  // of their own — throw it away, or stop and trim before sending.
  if (recording?.locked) {
    return (
      <>
        <button
          type="button"
          className="record-discard"
          onClick={recorder.discard}
          aria-label="Удалить запись"
        >
          <TrashIcon />
        </button>
        <button
          type="button"
          className="record-stop"
          onClick={recorder.stopForTrim}
          aria-label="Остановить запись"
        >
          {/* A padlock, not a pause: what this button ends is the locked take,
              and pause is a promise it does not keep — pressing it stops the
              recording and opens the trim screen. */}
          <LockIcon />
        </button>
      </>
    );
  }

  return (
    <div className="record-anchor">
      {/* «Проведите вверх — закрепить», without the sentence: the arc is the
          path the thumb takes and the padlock is what it reaches. A line of
          text sat here for a gesture that is already in progress, and it had to
          be read — this is understood at a glance and clears the thumb, which
          is the only part of the screen anyone is looking at by now. */}
      {recording && recording.state !== 'joining' && (
        <div className="record-lock-hint" aria-hidden="true">
          <SwipeUpArcIcon />
          <span className="record-lock-pill">
            <LockIcon size={13} />
          </span>
        </div>
      )}
      <button
        type="button"
        // Red means recording, so it waits for the device the same way the pill
        // does. Until then the button is still the accent one that was pressed.
        className={`record-button${recording?.state === 'capturing' ? ' record-button-live' : ''}`}
        // Touch-action is what stops the browser scrolling the thread instead
        // of reading the slide-up as a lock.
        // The words the tooltip used to carry live here now that the hint above
        // is drawn rather than written: what the icons say to a thumb, this says
        // to anyone the icons cannot reach.
        title={
          recording
            ? recording.state === 'capturing'
              ? 'Идёт запись: вверх — закрепить, влево — отменить'
              : recording.state === 'joining'
                ? 'Собираем видео из частей'
                : 'Включаем устройство'
            : 'Тап — переключить микрофон и камеру, удержание — запись'
        }
        aria-label={
          recording ? 'Идёт запись' : mode === 'voice' ? 'Записать голосовое' : 'Записать кружочек'
        }
        {...recorder.buttonProps}
      >
        {icon}
      </button>
    </div>
  );
}
